import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getGuideList, getGuideDetail, deleteGuide } from '../../api/senaApi';
import { isFrontSlot, getFormationForRound, FORMATION_LABEL } from './senaFormation';
import { heroPortrait as heroImg, skillImg, SkillPosLabel } from './senaAssets';
import './SenaSiege.css';

const BOSSES = [
  { key: 'eye', label: '파멸의눈동자', boss: '파멸의눈동자' },
  { key: 'umawang', label: '우마왕', boss: '우마왕' },
  { key: 'steel', label: '강철의포식자', boss: '강철의포식자' },
];

export default function SenaRaid() {
  const navigate = useNavigate();
  const [selectedBoss, setSelectedBoss] = useState('eye');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setSelectedGuide(null);
    getGuideList('raid', selectedBoss)
      .then(r => setPosts(r.data || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [selectedBoss]);

  const handleSelectPost = async (guideId) => {
    setDetailLoading(true);
    try {
      const res = await getGuideDetail(guideId);
      setSelectedGuide(res.data);
    } catch { alert('공략을 불러올 수 없습니다.'); }
    finally { setDetailLoading(false); }
  };

  const calcTurns = (guide) => {
    if (!guide?.skills) return {};
    const mult = guide.includeEnemySkill === 'Y' ? 2 : 4;
    const byRound = {};
    guide.skills.forEach(sk => {
      if (!byRound[sk.roundNo]) byRound[sk.roundNo] = [];
      byRound[sk.roundNo].push(sk);
    });
    const result = {};
    let startTurn = 0;
    for (let r = 1; r <= guide.totalRounds; r++) {
      const skills = (byRound[r] || []).sort((a, b) => a.seqNo - b.seqNo);
      result[r] = skills.map((sk, idx) => ({ ...sk, turn: startTurn + idx * mult }));
      if (skills.length > 0) startTurn = startTurn + (skills.length - 1) * mult;
    }
    return result;
  };

  return (
    <div className="sena-page">
      <header className="sena-header">
        <button className="siege-back-btn" onClick={() => navigate('/sena')}>← 메인으로</button>
        <h1 className="sena-header-guild">레이드 (일반)</h1>
        <p className="sena-header-sub">보스별 공략</p>
      </header>

      <div className="siege-body">
        <nav className="siege-day-nav">
          {BOSSES.map((b) => (
            <button key={b.key} className={`siege-day-btn ${selectedBoss === b.key ? 'active' : ''}`}
              onClick={() => setSelectedBoss(b.key)}>
              <span className="siege-day-label">{b.label}</span>
            </button>
          ))}
        </nav>

        <main className="siege-content">
          {selectedGuide ? (
            <GuideDetailView guide={selectedGuide} loading={detailLoading} calcTurns={calcTurns}
              onBack={() => setSelectedGuide(null)}
              onEdit={() => navigate(`/sena/guide/write?type=raid&sub=${selectedBoss}&id=${selectedGuide.guideId}`, { state: { guideData: selectedGuide } })}
              onDelete={() => {
                if (!window.confirm('삭제하시겠습니까?')) return;
                deleteGuide(selectedGuide.guideId).then(() => {
                  setPosts(prev => prev.filter(p => p.guideId !== selectedGuide.guideId));
                  setSelectedGuide(null);
                }).catch(() => alert('삭제 실패'));
              }}
            />
          ) : (
            <div className="siege-list">
              <div className="siege-list-header">
                <h2 className="siege-list-title">{BOSSES.find(b => b.key === selectedBoss)?.boss}</h2>
                <div className="siege-list-actions">
                  <span className="siege-list-count">{posts.length}건</span>
                  <button className="siege-write-btn" onClick={() => navigate(`/sena/guide/write?type=raid&sub=${selectedBoss}`)}>글쓰기</button>
                </div>
              </div>
              {loading ? (
                <div className="siege-empty"><p>조회 중...</p></div>
              ) : posts.length === 0 ? (
                <div className="siege-empty"><p>아직 등록된 공략이 없습니다.</p></div>
              ) : (
                <div className="siege-post-list">
                  {posts.map(post => (
                    <button key={post.guideId} className="siege-post-card" onClick={() => handleSelectPost(post.guideId)}>
                      <strong className="siege-post-title">{post.title}</strong>
                      <div className="siege-post-meta">
                        <span>{post.authorDisplayNm || post.authorNm}</span>
                        <span>{new Date(post.createdAt).toLocaleDateString()}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function GuideDetailView({ guide, loading, calcTurns, onBack, onEdit, onDelete }) {
  if (loading) return <div className="siege-empty"><p>로딩 중...</p></div>;
  if (!guide) return null;
  const turnsByRound = calcTurns(guide);
  const canModify = localStorage.getItem('userId') === guide.authorNm || localStorage.getItem('userRole') === 'ADMIN';

  return (
    <div className="siege-detail">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <button className="siege-detail-back" onClick={onBack}>← 목록으로</button>
        {canModify && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="siege-write-btn" onClick={onEdit}>수정</button>
            <button className="gw-war-del-btn" onClick={onDelete}>삭제</button>
          </div>
        )}
      </div>
      <h2 className="siege-detail-title">{guide.title}</h2>
      <div className="siege-detail-meta">
        <span>{guide.authorDisplayNm || guide.authorNm}</span>
        <span>{new Date(guide.createdAt).toLocaleDateString()}</span>
        <span>적스킬: {guide.includeEnemySkill === 'Y' ? '포함' : '미포함'}</span>
      </div>

      {/* 진형 및 세팅 — 라운드별 */}
      {Array.from({ length: guide.totalRounds || 1 }, (_, i) => i + 1).map(roundNo => {
        const roundAllies = (guide.allies || []).filter(a => (a.roundNo || 1) === roundNo);
        if (roundAllies.length === 0) return null;
        const formation = getFormationForRound(guide, roundNo);
        const formationLabel = FORMATION_LABEL[formation] || '';
        const showRoundHeader = (guide.totalRounds || 1) > 1;
        return (
          <div key={roundNo} className="gd-section">
            <h3 className="gd-section-title">
              {showRoundHeader ? `라운드 ${roundNo} 진형 및 세팅` : '진형 및 세팅'}
              {formationLabel && <span style={{ fontSize: 13, color: '#8a6a50', marginLeft: 8, fontWeight: 400 }}>({formationLabel})</span>}
            </h3>
            <div className="gd-setting-table">
              <div className="gd-setting-header">
                <span className="gd-sh-slot">배치</span>
                <span className="gd-sh-hero">영웅</span>
                <span className="gd-sh-field">세트</span>
                <span className="gd-sh-field">치확</span>
                <span className="gd-sh-field">약확</span>
                <span className="gd-sh-field">속공</span>
                <span className="gd-sh-note">기타</span>
              </div>
              {roundAllies.map(ally => {
                const img = heroImg(ally.heroNm, 'ALLY');
                const setLabel = ['-','복수자','암살자','성기사','수문장','추적자','주술사','조율자','선봉장','수호자'][Number(ally.setOption)] || '-';
                const speedLabel = ['상관없음','1순위','2순위','3순위','4순위','5순위','직접입력'][Number(ally.speedOption)] || '-';
                const showSet = ally.setOption && ally.setOption !== '0';
                const showCrit = ally.critRate && ally.critRate > 0;
                const showWeak = ally.weakRate && ally.weakRate > 0;
                const showSpeed = ally.speedOption && ally.speedOption !== '0';
                const front = isFrontSlot(ally.slotNo, formation);
                return (
                  <div key={ally.guideAllyId} className="gd-setting-row">
                    <span className={`gd-sr-slot ${front ? 'front' : 'back'}`}>
                      {front ? '전열' : '후열'}
                    </span>
                    <span className="gd-sr-hero">
                      {img ? <img className="gd-sr-img" src={img} alt={ally.heroNm} /> : null}
                      {ally.heroNm}
                    </span>
                    <span className="gd-sr-field">{showSet ? setLabel : '-'}</span>
                    <span className="gd-sr-field">{showCrit ? `${ally.critRate}%` : '-'}</span>
                    <span className="gd-sr-field">{showWeak ? `${ally.weakRate}%` : '-'}</span>
                    <span className="gd-sr-field">{showSpeed ? (ally.speedOption === '6' ? ally.speedCustom : speedLabel) : '-'}</span>
                    <span className="gd-sr-note">{ally.note || ''}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {Array.from({ length: guide.totalRounds }, (_, i) => i + 1).map(roundNo => {
        const skills = turnsByRound[roundNo] || [];
        return (
          <div key={roundNo} className="gd-section">
            <h3 className="gd-section-title">라운드 {roundNo}</h3>
            {skills.length === 0 ? (
              <p className="gd-empty">스킬 없음</p>
            ) : (
              <div className="gd-skill-flow">
                {skills.map((sk, idx) => {
                  const img = skillImg(sk.heroNm, sk.heroType, sk.skillNo);
                  return (
                    <div key={idx} className={`gd-skill-card ${sk.isEnemy === 'Y' ? 'enemy' : 'ally'}`}>
                      <span className={`gd-skill-turn ${[20,40,60].includes(sk.turn) ? 'milestone' : ''}`}>{sk.turn}턴</span>
                      {sk.isEnemy !== 'Y' && (
                        <span className="gd-skill-hero-pos">{sk.heroNm}<br/><SkillPosLabel skillNo={sk.skillNo} /></span>
                      )}
                      {img ? <img className="gd-skill-img" src={img} alt={sk.skillNm} /> : (
                        <div className="gd-skill-text">
                          <span className="gd-skill-hero">{sk.isEnemy === 'Y' ? `[적]${sk.heroNm}` : sk.heroNm}</span>
                          <span className="gd-skill-name">스킬{sk.skillNo}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {guide.description && (
        <div className="gd-section">
          <h3 className="gd-section-title">설명</h3>
          <div className="gd-description">
            {guide.description.split('\n').map((line, i) => (
              <p key={i}>{line || <br />}</p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
